// "Today on Uptrack": real global totals from GET /api/public/stats, shown in
// the hero stage. No numbers are ever made up: while loading it shows
// placeholders, and if the API does not answer the panel is not shown.
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiV2, type PublicStats } from '@/lib/api-v2-client'
import { cn } from '@/lib/utils'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

const fmt = (n: number) => n.toLocaleString('en-US')

const DAY_MS = 86_400_000

/**
 * Today's checks counted forward from the last real total at today's average
 * rate (total / seconds since 00:00 UTC), so the number moves every second.
 * The forward count stops at 2 minutes and at UTC midnight; the next real
 * total (every 30 s) replaces it. Pure.
 */
export function projectedChecks(today: number, updatedAt: string, nowMs: number): number {
  const updated = Date.parse(updatedAt)
  if (Number.isNaN(updated)) return today
  const midnight = Math.floor(updated / DAY_MS) * DAY_MS
  const rate = today / Math.max(60, (updated - midnight) / 1000)
  const ahead = Math.min(Math.max(0, nowMs - updated), 120_000, midnight + DAY_MS - updated) / 1000
  return Math.floor(today + rate * ahead)
}

/** The live checks number: ticks every second and never counts down within one UTC day. */
function useLiveChecks(stats?: PublicStats): number | undefined {
  const [shown, setShown] = useState<{ day: number; value: number }>()
  useEffect(() => {
    if (!stats) return
    const day = Math.floor(Date.parse(stats.updated_at) / DAY_MS)
    const id = window.setInterval(() => {
      const projected = projectedChecks(stats.checks.today, stats.updated_at, Date.now())
      setShown((prev) => (prev && prev.day === day ? { day, value: Math.max(prev.value, projected) } : { day, value: projected }))
    }, 1000)
    return () => window.clearInterval(id)
  }, [stats])
  if (!stats) return undefined
  const day = Math.floor(Date.parse(stats.updated_at) / DAY_MS)
  return shown && shown.day === day ? Math.max(shown.value, stats.checks.today) : stats.checks.today
}

type Cell = { label: string; value?: string; sub?: string; tone?: string }

export function cellsOf(stats?: PublicStats, liveChecks?: number): Cell[] {
  const checks = liveChecks ?? stats?.checks.today
  return [
    { label: 'Checks', value: checks === undefined ? undefined : fmt(checks), sub: stats && `${fmt(stats.checks.yesterday)} yesterday` },
    {
      label: 'Outages',
      value: stats && fmt(stats.outages.today),
      sub: stats && `${fmt(stats.outages.yesterday)} yesterday`,
      // Red only when there was an outage; a zero is good news.
      tone: stats && stats.outages.today > 0 ? 'text-status-down' : undefined,
    },
    { label: 'Alerts sent', value: stats && fmt(stats.alerts.today), sub: stats && `${fmt(stats.alerts.yesterday)} yesterday` },
    { label: 'Check locations', value: MONITOR_LOCATIONS.stat.value, sub: MONITOR_LOCATIONS.stat.sub },
  ]
}

export function LiveStats({ className }: { className?: string }) {
  const { data, isError } = useQuery({
    queryKey: ['public-stats'],
    queryFn: () => apiV2.getPublicStats(),
    refetchInterval: 30_000,
    staleTime: 15_000,
    retry: 1,
  })
  const stats = data?.data
  const liveChecks = useLiveChecks(stats)
  if (isError) return null
  return (
    // data-md="skip": the numbers load in the browser, so the server HTML has only labels (the machine view leaves it out).
    <section aria-label="Today on Uptrack" data-md="skip" className={cn('rounded-lg border border-border bg-card', className)}>
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5 text-sm">
        <span className="h-2 w-2 rounded-full bg-status-up" aria-hidden="true" />
        <span className="font-medium text-foreground">Live</span>
        <span className="text-muted-foreground">· Today on Uptrack (UTC)</span>
      </div>
      <dl className="grid grid-cols-2">
        {cellsOf(stats, liveChecks).map((c, i) => (
          <div key={c.label} className={cn('px-4 py-3', i % 2 === 1 && 'border-l border-border', i > 1 && 'border-t border-border')}>
            <dt className="text-sm text-muted-foreground">{c.label}</dt>
            <dd className={cn('num mt-0.5 text-2xl font-semibold tracking-tight', c.tone ?? 'text-foreground')}>
              {c.value ?? <span className="inline-block h-7 w-20 animate-pulse rounded-sm bg-muted align-middle" />}
            </dd>
            <dd className="num text-sm text-muted-foreground">{c.sub ?? ' '}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}
